import * as React from 'react';
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import backgroundImg from '../../components/imgs/background.jpg'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import theme from '../login/themeLogin';
import { grey, red } from '@mui/material/colors';
import { Paper } from '@mui/material';
import api from '../../services/api';

const MIN_PASSWORD_LEN = 8

export default function ChangePassword() {
  const queryParam = useQuery();
  const navigate = useNavigate()
  const auth = useAuth()
  const token = queryParam.get("hash")

  React.useEffect(() => {
    if (auth.user != null) {
      navigate('/')
    }
  }, [auth.user, navigate])

  React.useEffect(() => {
    document.title = "IPV7 Tools - Alterar senha"
  }, [])

  const [errorInput, setErrorInput] = React.useState({
    email: false,
    password: false,
    passwordConfirm: false,
  })
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [errorMsg, setErrormsg] = React.useState({
    severity: "error",
    msg: "",
    show: false
  })

  // Link sem ?hash= não tem como redefinir nada — avisa em vez de deixar
  // o usuário preencher o formulário à toa.
  React.useEffect(() => {
    if (!token) {
      setErrormsg({
        show: true,
        severity: 'error',
        msg: "Link de recuperação inválido. Solicite um novo email de recuperação.",
      })
    }
  }, [token])

  const handleSubmit = (event) => {
    event.preventDefault();
    if (saving || saved) return;

    setErrorInput({ email: false, password: false, passwordConfirm: false })
    setErrormsg(errorMsg => ({ ...errorMsg, show: false }))

    const data = new FormData(event.currentTarget);
    const email = (data.get('email') || '').trim()
    const password = data.get('password') || ''
    const passwordConfirm = data.get('passwordConfirm') || ''

    if (!token) {
      setErrormsg({
        show: true,
        severity: 'error',
        msg: "Link de recuperação inválido. Solicite um novo email de recuperação.",
      })
      return;
    }
    if (!email || email.length < 5 || !email.includes('@')) {
      setErrorInput(obj => ({ ...obj, email: true }))
      return;
    }
    if (password.length < MIN_PASSWORD_LEN) {
      setErrorInput(obj => ({ ...obj, password: true }))
      return;
    }
    if (password !== passwordConfirm) {
      setErrorInput(obj => ({ ...obj, passwordConfirm: true }))
      setErrormsg({ show: true, msg: "As senhas não conferem.", severity: 'warning' })
      return;
    }

    setSaving(true)
    api.put("recovery/", { email, password, token }).then(res => {
      // O backend devolve 200 com {"errors": [...]} quando a validação falha.
      const errors = res.data?.errors
      if (errors?.length) {
        setErrormsg({ show: true, severity: 'warning', msg: errors[0].msg })
        return;
      }
      setSaved(true)
      setErrormsg({ show: true, msg: "Sua senha foi trocada com sucesso!", severity: 'success' })
      setTimeout(() => navigate('/login'), 4000)
    }).catch(err => {
      // Token inválido/expirado vinha como 401 e o interceptor global jogava
      // o usuário no /login exibindo "sucesso". Agora a falha aparece aqui.
      const status = err.response?.status
      if (status === 400 || status === 401) {
        setErrormsg({
          show: true,
          severity: 'error',
          msg: "Link de recuperação inválido ou expirado (ele vale por 1 hora). "
            + "Confira se o email digitado é o mesmo que recebeu o link e, se precisar, solicite um novo.",
        })
      } else if (!status) {
        setErrormsg({
          show: true,
          severity: 'error',
          msg: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.",
        })
      } else {
        setErrormsg({
          show: true,
          severity: 'error',
          msg: "Não foi possível alterar sua senha. Tente novamente em instantes.",
        })
      }
    }).finally(() => setSaving(false))
  };

  return (
    <ThemeProvider theme={theme}>

      <div component="main" maxWidth={false}
        style={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
          justifyContent: 'center',
          height: '100vh',
          backgroundImage: `url(${backgroundImg})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <CssBaseline />
        <Box minWidth={300} sx={{
          display: 'flex',
          mr: { xs: 0, sm: 0, md: 20 }
        }}
        >
          <Paper elevation={2} component="form" onSubmit={handleSubmit} noValidate sx={{
            mt: 1,
            width: { sm: 500, md: 500 },
            p: 5,
            m: 5,
            backgroundColor: 'rgba(242, 242, 242, 1)',
            position: 'relative',
          }}>
            <Typography variant="h6" sx={{ color: grey[900], mb: 1 }}>
              Cadastrar nova senha
            </Typography>
            <TextField
              error={errorInput.email}
              margin="normal"
              required
              fullWidth
              name="email"
              label="Insira seu email"
              type="email"
              id="email"
              autoComplete="email"
              autoFocus
              disabled={saving || saved}
              helperText={errorInput.email ? "Verifique este campo." : ''}
            />
            <TextField
              error={errorInput.password}
              margin="normal"
              required
              fullWidth
              name="password"
              label="Insira sua nova senha"
              type="password"
              id="password"
              autoComplete="new-password"
              disabled={saving || saved}
              helperText={errorInput.password
                ? `A senha deve ter no mínimo ${MIN_PASSWORD_LEN} caracteres.`
                : `Mínimo de ${MIN_PASSWORD_LEN} caracteres.`}
            />
            <TextField
              error={errorInput.passwordConfirm}
              margin="normal"
              required
              fullWidth
              name="passwordConfirm"
              label="Confirme sua nova senha"
              type="password"
              id="passwordConfirm"
              autoComplete="new-password"
              disabled={saving || saved}
              helperText={errorInput.passwordConfirm ? "As senhas não conferem." : ''}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={saving || saved}
              sx={{
                mt: 3, mb: 2,
                backgroundColor: grey[900],
              }}
            >
              {saving ? "Salvando..." : "Alterar senha"}
            </Button>
            <Link to={"/recovery"} style={{ textDecoration: 'none', color: red[900] }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Solicitar um novo link
              </Typography>
            </Link>
            <Alert severity={errorMsg.severity}
              sx={{ display: errorMsg.show ? '' : 'none', mt: 2 }}
            >{errorMsg.msg}</Alert>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>

  );
}


function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}
